import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, deleteDoc, deleteField, doc, getDocsFromServer, limit, onSnapshot, orderBy, query, serverTimestamp, setDoc, startAfter, updateDoc, where } from 'firebase/firestore';
import { auth, firestore } from '../firebaseConfig';
import { TRANSACTION_STATUS, TRANSACTION_TYPES, buildFirestoreTransaction, fromFirestoreTransaction, getRealizedStatusForType, toFirestoreStatus, toFirestoreYearMonth, isValidCivilDate } from '../domain/finance';
import { awaitRemoteConfirmation, markServiceAvailable, markServiceUnavailable, requireConnectionForMutation, runRemoteOperation } from '../services/connectivityService';

const PAGE_SIZE = 100;

export const mergeTransactionsById = (current = [], incoming = []) => {
  const byId = new Map(current.map((item) => [item.id, item]));
  incoming.forEach((item) => byId.set(item.id, item));
  return Array.from(byId.values());
};

const buildReadConstraints = ({ yearMonth, hasValidRange, rangeStart, rangeEnd, allPeriods }) => (
  hasValidRange
    ? [
      ...(rangeStart ? [where('date', '>=', rangeStart)] : []),
      ...(rangeEnd ? [where('date', '<=', rangeEnd)] : []),
    ]
    : allPeriods
      ? []
      : [where('yearMonth', '==', yearMonth)]
);

export function useTransactions(accountId, selectedPeriod, { startDate = '', endDate = '', allPeriods = false, enabled = true, complete = false } = {}) {
  const [queryState, setQueryState] = useState({
    transactions: [],
    resolvedRequestKey: null,
    resolvedScopeKey: null,
    resolvedPeriod: null,
    error: null,
    freshness: 'loading',
    coverage: 'unknown',
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [retryVersion, setRetryVersion] = useState(0);
  const lastVisibleRef = useRef(null);
  const queryGenerationRef = useRef(0);
  const loadMoreGenerationRef = useRef(0);
  const yearMonth = toFirestoreYearMonth(selectedPeriod);
  const rangeStart = isValidCivilDate(startDate) ? startDate : '';
  const rangeEnd = isValidCivilDate(endDate) ? endDate : '';
  const hasValidRange = Boolean(rangeStart || rangeEnd)
    && (!rangeStart || !rangeEnd || rangeStart <= rangeEnd);
  const hasValidRequest = Boolean(enabled && accountId && (yearMonth || hasValidRange || allPeriods));
  const authenticatedUid = auth.currentUser?.uid || '';
  const scopeKey = JSON.stringify([
    complete,
    authenticatedUid,
    accountId || '',
    allPeriods ? 'all' : yearMonth || '',
    rangeStart,
    rangeEnd,
  ]);
  const requestKey = JSON.stringify([
    enabled,
    complete,
    accountId || '',
    allPeriods ? 'all' : yearMonth || '',
    rangeStart,
    rangeEnd,
    retryVersion,
  ]);
  const loading = hasValidRequest && queryState.resolvedRequestKey !== requestKey && !queryState.error;
  const { transactions, error, resolvedPeriod, freshness, coverage } = queryState;

  useEffect(() => {
    const queryGeneration = queryGenerationRef.current + 1;
    queryGenerationRef.current = queryGeneration;
    loadMoreGenerationRef.current += 1;
    let active = true;
    let unsubscribe;
    let snapshotVersion = 0;
    let completeReadInFlight = false;
    let completeReadInFlightSignature = null;
    let pendingCompleteSnapshot = null;
    let lastCompletedSnapshotSignature = null;
    let sawCacheSinceLastServerRead = false;

    if (!hasValidRequest) {
      lastVisibleRef.current = null;
      setQueryState({
        transactions: [],
        resolvedRequestKey: requestKey,
        resolvedScopeKey: scopeKey,
        resolvedPeriod: null,
        error: null,
        freshness: 'loading',
        coverage: 'unknown',
      });
      setLoadingMore(false);
      setHasMore(false);
      return () => {
        active = false;
        if (queryGenerationRef.current === queryGeneration) queryGenerationRef.current += 1;
      };
    }

    setQueryState((current) => (current.resolvedScopeKey === scopeKey
      ? { ...current, error: null }
      : {
        transactions: [], resolvedRequestKey: null, resolvedScopeKey: scopeKey,
        resolvedPeriod: selectedPeriod, error: null, freshness: 'loading', coverage: 'unknown',
      }));
    setLoadingMore(false);
    setHasMore(false);
    lastVisibleRef.current = null;

    const failRequest = (snapshotError) => {
      if (!active || queryGeneration !== queryGenerationRef.current) return;
      console.error('Erro ao carregar movimentações do Firestore:', {
        accountId,
        code: snapshotError?.code || 'unknown',
        yearMonth: allPeriods ? 'all-periods' : yearMonth,
        message: snapshotError?.message || 'Falha na consulta',
      });
      lastVisibleRef.current = null;
      setHasMore(false);
      setQueryState((current) => {
        const permissionRevoked = String(snapshotError?.code || '').includes('permission-denied');
        const canPreserve = !permissionRevoked && current.resolvedScopeKey === scopeKey && current.coverage === 'complete';
        return {
        ...current,
        transactions: canPreserve ? current.transactions : [],
        resolvedRequestKey: canPreserve ? requestKey : null,
        resolvedScopeKey: scopeKey,
        resolvedPeriod: selectedPeriod,
        error: snapshotError,
        freshness: canPreserve ? 'stale' : 'unavailable',
        coverage: canPreserve ? 'complete' : 'unknown',
      };
      });
      if (/unavailable|network|deadline-exceeded/.test(String(snapshotError?.code || ''))) markServiceUnavailable();
    };

    try {
      const periodQuery = query(
        collection(firestore, 'accounts', accountId, 'transactions'),
        ...buildReadConstraints({ yearMonth, hasValidRange, rangeStart, rangeEnd, allPeriods }),
        orderBy('date', 'desc'),
        limit(PAGE_SIZE)
      );
      const readCompleteSnapshot = async (snapshot, snapshotSignature) => {
        const version = ++snapshotVersion;
        completeReadInFlight = true;
        completeReadInFlightSignature = snapshotSignature;
        setQueryState((current) => ({ ...current, error: null }));
        try {
          let page = snapshot;
          const documents = [...page.docs];
          let pages = 1;
          while (page.size === PAGE_SIZE) {
            if (pages >= 1000 || !page.docs.at(-1)) throw new Error('TRANSACTION_PAGE_LIMIT_EXCEEDED');
            page = await getDocsFromServer(query(
              collection(firestore, 'accounts', accountId, 'transactions'),
              ...buildReadConstraints({ yearMonth, hasValidRange, rangeStart, rangeEnd, allPeriods }),
              orderBy('date', 'desc'), startAfter(page.docs.at(-1)), limit(PAGE_SIZE)
            ));
            if (!active || queryGeneration !== queryGenerationRef.current || version !== snapshotVersion) return;
            documents.push(...page.docs);
            pages += 1;
          }
          if (!active || queryGeneration !== queryGenerationRef.current || version !== snapshotVersion) return;
          setHasMore(false);
          setQueryState({
            transactions: mergeTransactionsById([], documents.map((item) => fromFirestoreTransaction(item.id, item.data()))),
            resolvedRequestKey: requestKey, resolvedScopeKey: scopeKey,
            resolvedPeriod: selectedPeriod, error: null, freshness: 'server', coverage: 'complete',
          });
          lastCompletedSnapshotSignature = snapshotSignature;
          markServiceAvailable();
        } catch (readError) {
          if (version === snapshotVersion) failRequest(readError);
        } finally {
          completeReadInFlight = false;
          completeReadInFlightSignature = null;
          const queued = pendingCompleteSnapshot;
          pendingCompleteSnapshot = null;
          if (queued && active && queryGeneration === queryGenerationRef.current) {
            void readCompleteSnapshot(queued.snapshot, queued.signature);
          }
        }
      };

      unsubscribe = onSnapshot(periodQuery, { includeMetadataChanges: true }, (snapshot) => {
        if (!active || queryGeneration !== queryGenerationRef.current) return;
        if (complete) {
          if (snapshot.metadata?.fromCache === true) {
            sawCacheSinceLastServerRead = true;
            setQueryState((current) => {
              const canPreserve = current.resolvedScopeKey === scopeKey && current.coverage === 'complete';
              return {
                ...current,
                transactions: canPreserve ? current.transactions : [],
                resolvedRequestKey: canPreserve ? requestKey : null,
                resolvedScopeKey: scopeKey,
                resolvedPeriod: selectedPeriod,
                error: canPreserve ? null : new Error('FINANCIAL_DATA_UNAVAILABLE_OFFLINE'),
                freshness: canPreserve ? 'stale' : 'unavailable',
                coverage: canPreserve ? 'complete' : 'unknown',
              };
            });
            return;
          }
          const snapshotSignature = JSON.stringify(snapshot.docs.map((item) => [item.id, item.data()]));
          const refreshAfterCache = sawCacheSinceLastServerRead;
          if (!refreshAfterCache && snapshotSignature === lastCompletedSnapshotSignature) return;
          sawCacheSinceLastServerRead = false;
          if (completeReadInFlight) {
            if (!refreshAfterCache && snapshotSignature === completeReadInFlightSignature) return;
            pendingCompleteSnapshot = { snapshot, signature: snapshotSignature };
            snapshotVersion += 1;
            return;
          }
          void readCompleteSnapshot(snapshot, snapshotSignature);
          return;
        }
        const fromCache = snapshot.metadata?.fromCache === true;
        if (fromCache && snapshot.empty) {
          setQueryState((current) => ({
            ...current,
            resolvedRequestKey: current.transactions.length ? requestKey : null,
            resolvedPeriod: selectedPeriod,
            error: current.transactions.length ? null : new Error('FINANCIAL_DATA_UNAVAILABLE_OFFLINE'),
            freshness: current.transactions.length ? 'stale' : 'unavailable',
            coverage: current.transactions.length ? current.coverage : 'unknown',
          }));
          return;
        }
        lastVisibleRef.current = snapshot.docs.at(-1) || null;
        setHasMore(snapshot.size === PAGE_SIZE);
        setQueryState({
          transactions: snapshot.docs.map((item) => fromFirestoreTransaction(item.id, item.data())),
          resolvedRequestKey: requestKey,
          resolvedScopeKey: scopeKey,
          resolvedPeriod: selectedPeriod,
          error: null,
          freshness: fromCache ? 'stale' : 'server',
          coverage: snapshot.size === PAGE_SIZE ? 'partial' : 'complete',
        });
        if (!fromCache) markServiceAvailable();
      }, (readError) => { snapshotVersion += 1; failRequest(readError); });
    } catch (snapshotError) {
      failRequest(snapshotError);
    }

    return () => {
      active = false;
      if (queryGenerationRef.current === queryGeneration) queryGenerationRef.current += 1;
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [accountId, yearMonth, hasValidRange, rangeStart, rangeEnd, allPeriods, enabled, complete, hasValidRequest, requestKey, scopeKey, selectedPeriod]);

  const loadMore = useCallback(async () => {
    if (!accountId || (!yearMonth && !hasValidRange && !allPeriods) || !lastVisibleRef.current || loadingMore) return;
    setLoadingMore(true);
    const queryGeneration = queryGenerationRef.current;
    const loadMoreGeneration = loadMoreGenerationRef.current + 1;
    loadMoreGenerationRef.current = loadMoreGeneration;
    try {
      const nextQuery = query(
        collection(firestore, 'accounts', accountId, 'transactions'),
        ...buildReadConstraints({ yearMonth, hasValidRange, rangeStart, rangeEnd, allPeriods }),
        orderBy('date', 'desc'),
        startAfter(lastVisibleRef.current),
        limit(PAGE_SIZE)
      );
      const snapshot = await getDocsFromServer(nextQuery);
      if (queryGeneration !== queryGenerationRef.current || loadMoreGeneration !== loadMoreGenerationRef.current) return;
      lastVisibleRef.current = snapshot.docs.at(-1) || lastVisibleRef.current;
      setHasMore(snapshot.size === PAGE_SIZE);
      markServiceAvailable();
      setQueryState((current) => ({
        ...current,
        transactions: mergeTransactionsById(
          current.transactions,
          snapshot.docs.map((item) => fromFirestoreTransaction(item.id, item.data()))
        ), freshness: 'server', coverage: snapshot.size === PAGE_SIZE ? 'partial' : 'complete',
      }));
    } catch (loadError) {
      if (queryGeneration === queryGenerationRef.current && loadMoreGeneration === loadMoreGenerationRef.current) {
        setQueryState((current) => ({ ...current, error: loadError }));
        if (/unavailable|network|deadline-exceeded/.test(String(loadError?.code || ''))) markServiceUnavailable();
      }
      throw loadError;
    } finally {
      if (queryGeneration === queryGenerationRef.current && loadMoreGeneration === loadMoreGenerationRef.current) {
        setLoadingMore(false);
      }
    }
  }, [accountId, yearMonth, loadingMore, hasValidRange, rangeStart, rangeEnd, allPeriods]);

  const addTransaction = useCallback(async (transaction) => {
    if (!accountId) throw new Error('ACCOUNT_REQUIRED');
    const payload = buildFirestoreTransaction(transaction);
    if (!payload) throw new Error('INVALID_TRANSACTION');
    const actorUid = auth.currentUser?.uid;
    if (!actorUid) throw new Error('AUTH_REQUIRED');
    requireConnectionForMutation();
    const operationId = String(transaction.clientOperationId || '').replace(/[^A-Za-z0-9_-]/g, '').slice(0, 96);
    const reference = operationId
      ? doc(firestore, 'accounts', accountId, 'transactions', `op_${operationId}`)
      : doc(collection(firestore, 'accounts', accountId, 'transactions'));
    const operationKey = `${actorUid}:${accountId}:create-transaction:${reference.id}`;
    await runRemoteOperation(operationKey, () => setDoc(reference, {
      ...payload,
      createdByUid: actorUid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }), transaction.confirmationTimeoutMs);
    return reference.id;
  }, [accountId]);

  const updateTransaction = useCallback(async (transaction) => {
    if (!accountId || !transaction.id) throw new Error('INVALID_TRANSACTION');
    const payload = buildFirestoreTransaction(transaction);
    if (!payload) throw new Error('INVALID_TRANSACTION');
    requireConnectionForMutation();
    await awaitRemoteConfirmation(updateDoc(doc(firestore, 'accounts', accountId, 'transactions', transaction.id), {
      ...payload,
      ...(transaction.attributedToUid === null ? { attributedToUid: deleteField(), attributedToNameSnapshot: deleteField() } : {}),
      updatedAt: serverTimestamp(),
    }));
  }, [accountId]);

  const deleteTransaction = useCallback(async (id) => {
    if (!accountId || !id) throw new Error('INVALID_TRANSACTION');
    requireConnectionForMutation();
    await awaitRemoteConfirmation(deleteDoc(doc(firestore, 'accounts', accountId, 'transactions', id)));
  }, [accountId]);

  const toggleTransactionStatus = useCallback(async (transaction) => {
    if (!accountId || !transaction.id) throw new Error('INVALID_TRANSACTION');
    const realizedStatus = getRealizedStatusForType(transaction.tipo);
    if (!realizedStatus) throw new Error('INVALID_TRANSACTION_TYPE');
    const nextStatus = transaction.status === TRANSACTION_STATUS.PENDING ? realizedStatus : TRANSACTION_STATUS.PENDING;
    requireConnectionForMutation();
    await awaitRemoteConfirmation(updateDoc(doc(firestore, 'accounts', accountId, 'transactions', transaction.id), {
      status: toFirestoreStatus(nextStatus),
      updatedAt: serverTimestamp(),
    }));
    return nextStatus;
  }, [accountId]);

  const receitas = useMemo(() => transactions.filter((item) => item.tipo === TRANSACTION_TYPES.INCOME), [transactions]);
  const despesas = useMemo(() => transactions.filter((item) => item.tipo === TRANSACTION_TYPES.EXPENSE), [transactions]);
  const retry = useCallback(() => setRetryVersion((version) => version + 1), []);

  const isResolved = hasValidRequest && queryState.resolvedRequestKey === requestKey && (!error || transactions.length > 0);
  return { isResolved, resolvedAccountId: isResolved ? accountId : null, receitas, despesas, transactions, allTransactions: transactions, loading, loadingMore, hasMore, error, resolvedPeriod, freshness, coverage, retry, loadMore, addTransaction, updateTransaction, deleteTransaction, toggleTransactionStatus };
}
